import React, {useEffect, useState} from 'react'
import {Box} from '@mui/material'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import {useToast} from 'src/components-new/feedback/ToastProvider'
import {STATUS} from 'src/constants/constants'
import apiClient from 'src/services/apiClient'
import {
  buildSecondaryAssumptionResponseBody,
  followTakenAt,
  getInitialResponseForm,
  isNextDoseEdited,
  isNextDoseValid,
  isResponseFormValid,
  isSecondaryAssumptionAnswered,
  SecondaryAssumption,
  SecondaryAssumptionResponseForm
} from 'src/constants/secondaryAssumptions'
import AssumptionResponseFormNew from './AssumptionResponseFormNew'
import NextDoseFieldsNew from './NextDoseFieldsNew'

// Second surface that can answer a reminder — the first being the deep-link page. Both exist
// because RF4.2 puts no deadline on an answer: an assumption stays answerable and correctable
// forever, so the list is not a read-only history but a place to fill in what was missed.

interface AssumptionResponseDialogNewProps {
  open: boolean
  assumption?: SecondaryAssumption
  onClose: () => void
  // Carries the document as the server actually persisted it (state/takenAt/bodyPart/respondedAt),
  // so the caller can patch its own list in place instead of refetching the whole window.
  // `nextDoseMoved` is true when the answer also moved the same therapy's next dose (RF4.5): its
  // derived row in the list is then stale, and the caller must refetch rather than patch in place.
  onSuccess: (updated?: SecondaryAssumption, nextDoseMoved?: boolean) => void
  /**
   * Opened from the list's round ✓ button: "taken" is already answered by that tap, so the form
   * starts on "yes" and skips the taken question entirely — only date/time (defaulted to the
   * expected one) and the body part remain to ask. Never set together with an already-answered
   * assumption; the quick button only ever appears on rows still awaiting a first answer.
   */
  presetTaken?: 'yes'
}

export default function AssumptionResponseDialogNew({
  open,
  assumption,
  onClose,
  onSuccess,
  presetTaken
}: AssumptionResponseDialogNewProps): JSX.Element {
  const toast = useToast()
  const [form, setForm] = useState<SecondaryAssumptionResponseForm>(getInitialResponseForm())
  const [statusSubmit, setStatusSubmit] = useState<string>()
  // RF4.4: the next dose as the server proposes it for this reminder, so answering from the diary
  // can also move it — exactly as the deep-link page does — without a second dialog.
  const [nextExpectedDate, setNextExpectedDate] = useState<string | undefined>(undefined)
  // once the patient edits the next dose by hand, moving takenAt no longer drags it along
  const [isNextDoseTouched, setIsNextDoseTouched] = useState(false)

  // Reset on every open so a previously edited row never leaks its form into the next one.
  useEffect(() => {
    if (!open) { return undefined }
    setForm(
      presetTaken ?
        {taken: presetTaken, takenAt: assumption?.expectedDate ?? null, bodyPart: []} :
        getInitialResponseForm(assumption)
    )
    setStatusSubmit(undefined)
    setNextExpectedDate(undefined)
    setIsNextDoseTouched(false)

    // The same source as the deep-link page: only the single GET carries the server-computed
    // nextExpectedDate (RF3.3), the list rows do not. A derived row has no _id - it IS the next
    // dose, moved with its own "Sposta" button - so there is nothing to fetch for it.
    if (!assumption?._id) { return undefined }
    let cancelled = false
    apiClient.getSecondaryAssumption(assumption._id, undefined)
      .then((data: SecondaryAssumption) => {
        if (cancelled || !data?.nextExpectedDate) { return }
        setNextExpectedDate(data.nextExpectedDate)
        setForm((current) => ({...current, nextExpectedDate: data.nextExpectedDate}))
      })
      // without it the dialog still answers the dose, just without moving the next one
      .catch(() => undefined)
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, assumption?._id, assumption?.expectedDate])

  // the reference an edited next dose is compared to (see isNextDoseEdited)
  const reference = assumption && {...assumption, nextExpectedDate}
  const isNextDoseMoved = isNextDoseEdited(form, reference)
  const isNextDoseOk = isNextDoseValid(form, reference)

  const onSubmit = async (): Promise<void> => {
    if (!assumption) { return }
    // RF4.4: an edited next dose travels in the same PATCH as the answer
    const body = buildSecondaryAssumptionResponseBody(form, reference)
    // RF4.3: the derived "next dose" row (no _id yet) is addressed by natural key instead.
    const updated = assumption._id
      ? await apiClient.patchSecondaryAssumption(assumption._id, body, setStatusSubmit)
      : await apiClient.patchSecondaryAssumptionByKey(
        {secondaryTherapyId: assumption.secondaryTherapyId, expectedDate: assumption.expectedDate as string},
        body,
        setStatusSubmit
      )
    if (!updated) {
      toast.error('Impossibile registrare la risposta. Riprova.')
      return
    }
    toast.success(isNextDoseMoved ? 'Risposta registrata e prossima assunzione spostata' : 'Risposta registrata')
    onSuccess(updated, isNextDoseMoved)
  }

  const wasAlreadyAnswered = isSecondaryAssumptionAnswered(assumption)

  return (
    <AppDialog
      open={open}
      onClose={onClose}
      title={assumption?.drugName ?? 'Terapia accessoria'}
      maxWidth="sm"
      actions={(
        <>
          <Button variant="secondary" onClick={onClose}>Annulla</Button>
          <Button
            variant="primary"
            loading={statusSubmit === STATUS.PENDING}
            disabled={!isResponseFormValid(form, reference) || !isNextDoseOk}
            onClick={onSubmit}
          >
            {presetTaken ? 'Conferma' : (wasAlreadyAnswered ? 'Aggiorna' : 'Invia')}
          </Button>
        </>
      )}
    >
      {assumption && (
        <AssumptionResponseFormNew
          assumption={assumption}
          form={form}
          onChange={(value) => setForm(isNextDoseTouched ? value : followTakenAt(form, value))}
          disabled={statusSubmit === STATUS.PENDING}
          hideTakenQuestion={Boolean(presetTaken)}
        />
      )}
      {/* RF3.3: only when the server says the next dose can be moved from this reminder */}
      {nextExpectedDate && (
        <Box sx={{mt: 3}}>
          <NextDoseFieldsNew
            value={form.nextExpectedDate ?? null}
            onChange={(value) => {
              setIsNextDoseTouched(true)
              setForm({...form, nextExpectedDate: value})
            }}
            disabled={statusSubmit === STATUS.PENDING}
            error={!isNextDoseOk}
          />
        </Box>
      )}
    </AppDialog>
  )
}
